import { uniqueId } from 'lodash-es'
import { useMemo } from 'react'

import { FvButton, ValidatedForm } from '@fv/client-components'
import type { ShippingLabelSize } from '@fv/client-types'

import { InputAdornment, InputGroup } from '@/components/inputs/InputGroup'
import arrayOfLength from '@/utils/arrayOfLength'

import { composeLabelsUrl } from './hooks'
import type { LabelConfig } from './labelConfig'

interface FormElements extends HTMLFormControlsCollection {
  count: HTMLSelectElement
  start?: HTMLSelectElement
}

type Props = {
  onClose: () => void
  defaultCount: number
  labelConfig: LabelConfig
  loadId: string
  pieceQuantity?: number
  unitQuantity: number
  onSubmit?: (props: {
    count: number
    loadId: string
    size: ShippingLabelSize
    start?: number
    isPieces?: boolean
  }) => void
}

export const LabelOptionForm = ({
  onClose,
  defaultCount,
  labelConfig,
  loadId,
  unitQuantity,
  pieceQuantity,
  onSubmit,
}: Props) => {
  const unitCountOptions = useUnitCountOptions(unitQuantity)
  const pieceCountOptions = usePieceCountOptions(pieceQuantity)
  const countOptions = [...unitCountOptions, ...pieceCountOptions]
  const defautlValue = useMemo(() => {
    return unitCountOptions.find(option => option.count === defaultCount)?.id
  }, [unitCountOptions, defaultCount])

  const onValidSubmit = (form: HTMLFormElement) => {
    const elements = form.elements as FormElements
    const selectedCountOption = countOptions.find(
      option => option.id === elements.count.value,
    )
    const countString = selectedCountOption?.count
    const startString = elements.start?.value ?? '1'
    const url = composeLabelsUrl({
      count: Number(countString),
      loadId,
      size: labelConfig.size,
      start: Number(startString) - 1,
      isPieces: selectedCountOption?.isPieces,
    })

    if (onSubmit) {
      onSubmit({
        count: Number(countString),
        loadId,
        size: labelConfig.size,
        start: Number(startString) - 1,
        isPieces: selectedCountOption?.isPieces,
      })
    } else {
      onClose()
      window.open(url, '_blank', 'noreferrer')
    }
  }

  return (
    <ValidatedForm className="flex-1" onValidSubmit={onValidSubmit}>
      {labelConfig.type === 'sheet' && labelConfig.count !== 1 && (
        <InputGroup
          inputType="select"
          startContent={
            <InputAdornment
              label="Position"
              position="start"
              className="w-24 !justify-start"
            />
          }
          inputProps={{
            className: 'form-control form-control--select',
            defaultValue: '1',
            name: 'start',
            options: buildStartOptions(labelConfig),
          }}
        />
      )}

      <InputGroup
        inputType="select"
        startContent={
          <InputAdornment
            label="Labels"
            position="start"
            className="w-24 !justify-start"
          />
        }
        inputProps={{
          className: 'form-control form-control--select',
          defaultValue: defautlValue ?? '',
          name: 'count',
          options: [
            convertToSelectOptions(unitCountOptions),
            convertToSelectOptions(pieceCountOptions),
          ],
          groups: ['Units', 'Pieces'],
        }}
      />
      <FvButton
        className="w-full justify-center"
        icon="tags"
        theme="primary"
        type="submit"
      >
        Build labels
      </FvButton>
    </ValidatedForm>
  )
}

const UNIT_COUNT_MULTIPLIERS = [1, 2, 4]
const useUnitCountOptions = (unitQuantity: number) => {
  return useMemo<LabelCountOption[]>(() => {
    return Array.from(
      new Set([
        1,
        ...UNIT_COUNT_MULTIPLIERS.map(multiplier => multiplier * unitQuantity),
      ]),
    ).map(count => ({
      id: uniqueId('count-'),
      count,
      isPieces: false,
    }))
  }, [unitQuantity])
}

const usePieceCountOptions = (pieceQuantity: number) => {
  return useMemo<LabelCountOption[]>(() => {
    return (pieceQuantity ? [pieceQuantity] : []).map(count => ({
      id: uniqueId('count-'),
      count,
      isPieces: true,
    }))
  }, [pieceQuantity])
}

function buildStartOptions(config: LabelConfig) {
  return arrayOfLength(config.count ?? 0).map(i => String(i + 1))
}

interface LabelCountOption {
  id: string
  count: number
  isPieces: boolean
}

const convertToSelectOptions = (options: LabelCountOption[]) => {
  return options.map(option => ({
    text: option.count.toString(),
    value: option.id,
  }))
}
